<template>
  <div>
    <AppWithLoading v-slot:default="{ localLoading, loader }">
      <div v-show="!localLoading">
        <StripePaymentCard v-if="newCard" style="width: 800px">
          <template #errors>
            <div v-if="error">
              <v-alert type="error">
                {{ error }}
              </v-alert>
            </div>
          </template>
          <template v-slot:validator="{ validate }">
            <div class="agree-block">
              <VCheckbox
                v-model="agreePublic"
                :label="$t('agreePublic')"
                class="agree-block__item"
              >
                <template #label>
                  <span
                    class="mr-1"
                  >{{ $t('agreePublic') }}
                    <a
                      target="_blank"
                      :href="offerLink"
                      @click="handleLinkClick"
                    >
                      {{ $t('offer') }}
                    </a>
                  </span>
                </template>
              </VCheckbox>
            </div>
            <div>
              <VBtn :disabled="!canProceed" @click="loader(() => validate() && confirm())">
                {{ $t('confirm') }}
              </VBtn>
            </div>
          </template>
        </StripePaymentCard>
        <template v-else>
          <div v-if="!newCard && (error || errorStripe)">
            <v-alert type="error">
              {{ error || errorStripe }}
            </v-alert>
          </div>
          <div class="agree-block">
            <VCheckbox
              v-model="agreePublic"
              :label="$t('agreePublic')"
              class="agree-block__item"
              @click="handleCheckboxClick"
            >
              <template #label>
                <span class="mr-1">
                  {{ $t('agreePublic') }}
                  <a
                    target="_blank"
                    :href="offerLink"
                    @click.stop
                    @mousedown.stop
                  >
                    {{ $t('offer') }}
                  </a>
                </span>
              </template>
            </VCheckbox>
          </div>
          <div class="px-2 px-sm-0">
            <VBtn :disabled="!canProceed" @click="loader(() => confirm())">
              {{ $t('confirm') }}
            </VBtn>
          </div>
        </template>
      </div>
    </AppWithLoading>
  </div>
</template>

<script>
import StripePaymentCard from '@/components/Payment/Payment/Stripe/StripePaymentCard';
import useAccount from '@/compositions/accounting/useAccount';
import useUpgradePlan from '@/compositions/accounting/useUpgradePlan';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import {
  computed, ref, toRefs, watch,
} from '@vue/composition-api';
import useStripe from '@/compositions/accounting/useStripe';
import useValidation from '@/compositions/useValidation';
import AppWithLoading from '@/components/AppWithLoading';
import { useI18n } from 'vue-i18n-composable';
import { quotaItemTypes, quotaTypes } from '@/enums';

export default {
  name: 'StripeQuotaPayment',
  components: {
    AppWithLoading,
    StripePaymentCard,
  },
  props: {
    newCard: {
      type: Boolean,
      default: false,
    },
    methodId: {
      type: Number,
      default: null,
    },
    quotaType: {
      type: String,
      required: true,
    },
    count: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const {
      count, quotaType, methodId, newCard,
    } = toRefs(props);

    const agreeRecurrent = ref(true);
    const agreePublic = ref(true);
    const error = ref(null);
    const { localLoading } = useValidation();
    const { locale } = useI18n();
    const linkClicked = ref(false);

    const offerLink = computed(() => (locale.value.toLowerCase() === 'ru'
      ? process.env.VUE_APP_RULES_URL
      : process.env.VUE_APP_CONFIDENTIAL_URL));

    const { account } = useAccount();
    const { plan: currentPlan } = account.value;
    const { priceUpgrade, quotasUpgrade } = useUpgradePlan();
    const { formatCurrencyFromMoney } = useNumberFormatter();
    const { quotaPayment, inputCompleted, error: errorStripe } = useStripe({
      onSuccess: () => {
        emit('success');
      },
      onFailure: (e) => {
        emit('error', e);
        error.value = e.message;
      },
    });

    function confirm() {
      return quotaPayment({
        count: count.value,
        itemType: quotaType.value,
        paymentMethodId: methodId.value,
      });
    }

    function canProceedByQuotaCount() {
      switch (quotaType.value) {
        case quotaItemTypes.CALLS: {
          return count.value >= 25;
        }
        case quotaItemTypes.SMS: {
          return count.value >= 50;
        }
        default: {
          return false;
        }
      }
    }

    const canProceed = computed(() => {
      return (
        agreePublic.value && (inputCompleted.value || methodId.value) && canProceedByQuotaCount()
      );
    });

    watch(newCard, () => {
      error.value = null;
    });

    const previousAgreePublicValue = ref(agreePublic.value);

    watch(agreePublic, (newValue) => {
      if (linkClicked.value) {
        if (newValue !== previousAgreePublicValue.value) {
          agreePublic.value = previousAgreePublicValue.value;
        }
        linkClicked.value = false;
      } else {
        previousAgreePublicValue.value = newValue;
      }
    });

    function handleLinkClick(event) {
      previousAgreePublicValue.value = agreePublic.value;
      linkClicked.value = true;
      event.stopPropagation();
      event.preventDefault();
      window.open(offerLink.value, '_blank');
    }

    return {
      priceUpgrade,
      formatCurrencyFromMoney,
      quotasUpgrade,
      agreeRecurrent,
      agreePublic,
      canProceed,
      currentPlan,
      confirm,
      error,
      localLoading,
      errorStripe,
      offerLink,
      locale: computed(() => locale.value.toLowerCase()),
      handleLinkClick,
    };
  },
};
</script>

<style lang="scss" scoped>
.agree-block {
  &__item {
    width: fit-content;
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .agree-block {
    padding: 0 0.5rem;
    &__item .v-label {
      font-size: 0.9375rem;
    }
  }
}
</style>

<i18n lang="yml" locale="ru">
agreeRecurrent: Я даю согласие на регулярные списание с карты
agreePublic: 'Принимаю условия публичной '
offer: оферты
confirm: Подтвердить оплату
</i18n>
<i18n lang="yml" locale="en">
agreeRecurrent: I understand that I will be charged on a regular basis
agreePublic: 'I accept the '
offer: terms and conditions
confirm: Confirm payment
</i18n>
